import { createAction, handleActions } from 'redux-actions';
import { replaceObjectInList } from 'utility/helpers';
import { sortAlphabetically } from 'utility/uiUtils';
import { isObject } from 'formik';
import http from '../../utility/http';
import {
  api_document_categories,
  API_DOCUMENT_CATEGORIES,
  api_document_category_types,
  API_DOCUMENT_TYPES,
  API_DOCUMENTS,
  api_documents,
  API_USER_DOCUMENT_CATEGORIES,
} from '../../constants/apiRoutes';

// ------------------------------------
// Constants
// ------------------------------------

const SET_DOCUMENT_CATEGORIES = 'SET_DOCUMENT_CATEGORIES';
const ADD_DOCUMENT_CATEGORY = 'ADD_DOCUMENT_CATEGORY';
const ADD_DOCUMENT = 'ADD_DOCUMENT';
const UPDATE_DOCUMENT = 'UPDATE_DOCUMENT';
const DELETE_DOCUMENT = 'DELETE_DOCUMENT';
const UPDATE_CATEGORY = 'UPDATE_CATEGORY';
const DELETE_DOCUMENT_CATEGORY = 'DELETE_DOCUMENT_CATEGORY';
const ADD_DOCUMENT_TYPES = 'ADD_DOCUMENT_TYPES';

const initialState = {
  documentCategories: [],
};

// ------------------------------------
// Actions
// ------------------------------------

export const setCategories = createAction(SET_DOCUMENT_CATEGORIES);
export const addCategory = createAction(ADD_DOCUMENT_CATEGORY);
export const addDocument = createAction(ADD_DOCUMENT);
export const updateDocument = createAction(UPDATE_DOCUMENT);
export const deleteDocument = createAction(DELETE_DOCUMENT);
export const deleteCategory = createAction(DELETE_DOCUMENT_CATEGORY);
export const updateCategory = createAction(UPDATE_CATEGORY);
export const addDocumentTypes = createAction(ADD_DOCUMENT_TYPES);
// ------------------------------------
// Reducers
// ------------------------------------

const reducers = {
  [SET_DOCUMENT_CATEGORIES]: (state, { payload }) => {
    return { ...state, documentCategories: payload };
  },
  [ADD_DOCUMENT_CATEGORY]: (state, { payload }) => ({
    ...state,
    documentCategories: sortAlphabetically([
      ...state.documentCategories,
      {
        ...payload,
      },
    ]),
  }),
  [ADD_DOCUMENT]: (state, { payload }) => {
    const updatedCategories = state.documentCategories.map(category => ({
      ...category,
      types: category.types.map(type => {
        return type.id !== payload.type
          ? type
          : {
              ...type,
              document: payload,
            };
      }),
    }));

    return { ...state, documentCategories: updatedCategories };
  },
  [UPDATE_DOCUMENT]: (state, { payload }) => {
    const updatedCategories = state.documentCategories.map(category => ({
      ...category,
      types: category.types.map(type => {
        return type.id !== payload.type
          ? type
          : {
              ...type,
              document: payload,
            };
      }),
    }));

    return { ...state, documentCategories: updatedCategories };
  },
  [DELETE_DOCUMENT]: (state, { payload }) => {
    const { typeId, documentId } = payload;

    const updatedCategories = state.documentCategories.map(category => {
      const typeIndex = category.types.findIndex(
        categoryType =>
          categoryType.id === typeId && categoryType.document?.id === documentId
      );

      return {
        ...category,
        types:
          typeIndex !== -1
            ? replaceObjectInList(category.types, typeIndex, {
                ...category.types[typeIndex],
                document: null,
              })
            : category.types,
      };
    });

    return { ...state, documentCategories: updatedCategories };
  },
  [UPDATE_CATEGORY]: (state, { payload }) => {
    const { documentCategories } = state;

    const index = documentCategories.findIndex(
      category => category.id === payload.id
    );
    const updatedCategory = { ...documentCategories[index], ...payload };
    const updatedCategories = replaceObjectInList(
      documentCategories,
      index,
      updatedCategory
    );
    return { ...state, documentCategories: updatedCategories };
  },
  [DELETE_DOCUMENT_CATEGORY]: (state, { payload }) => {
    const categories = state.documentCategories.filter(
      category => category.id !== payload
    );
    return { ...state, documentCategories: categories };
  },
};

// ------------------------------------
// API calls
// ------------------------------------

export const getDocumentCategories = dispatch => {
  return http.get(API_DOCUMENT_CATEGORIES).then(({ data }) => {
    dispatch(setCategories(data));
  });
};

export const getPersonDocumentCategories = (dispatch, userId) => {
  return http
    .get(`${API_USER_DOCUMENT_CATEGORIES}${userId}/`)
    .then(({ data }) => {
      dispatch(setCategories(data));
    });
};

export const postDocumentCategories = (dispatch, params = {}) => {
  const { name, expire_warning_days, ...types } = params;

  return http
    .post(API_DOCUMENT_CATEGORIES, { name, expire_warning_days })
    .then(async ({ data }) => {
      const documentTypes = await http.post(API_DOCUMENT_TYPES, [
        ...types.types.map(dType => ({ ...dType, category: data.id })),
      ]);
      const createdCategory = {
        ...data,
        expire_warning_days,
        types: sortAlphabetically(documentTypes?.data || []),
      };

      return dispatch(addCategory(createdCategory));
    });
};

export const patchDocumentCategories = (dispatch, params = {}) => {
  const { name, expire_warning_days, selectedCategory, types } = params;

  return http
    .patch(api_document_categories(selectedCategory.id), {
      name,
      expire_warning_days,
    })
    .then(async () => {
      await http.put(api_document_category_types(selectedCategory.id), types);

      dispatch(
        updateCategory({
          id: selectedCategory.id,
          name,
          expire_warning_days,
          types,
        })
      );

      return {
        ...selectedCategory,
        name,
        expire_warning_days,
        types: sortAlphabetically(types || []),
      };
    });
};

export const postDocument = (dispatch, params = {}) => {
  const { file, ...restData } = params;

  if (isObject(file) || !file) {
    const formData = new FormData();
    formData.append('file', params.file || '');
    formData.append('user', params.user);
    formData.append('type', params.type);
    formData.append('description', params.description);
    formData.append('signing_date', params.signing_date || '');
    formData.append('expire_date', params.expire_date || '');

    return http.post(API_DOCUMENTS, formData).then(({ data }) => {
      dispatch(addDocument(data));
      return data;
    });
  }

  return http.post(API_DOCUMENTS, restData).then(({ data }) => {
    dispatch(addDocument(data));
    return data;
  });
};

export const patchDocument = (dispatch, params = {}) => {
  const { documentId, file, ...restData } = params;
  const requestData = { ...restData };

  if (isObject(file) || !file) {
    const formData = new FormData();
    Object.entries(requestData).forEach(([key, value]) => {
      formData.append(key, value || '');
    });
    formData.append('file', file || '');

    return http.patch(api_documents(documentId), formData).then(({ data }) => {
      dispatch(updateDocument(data));
      return data;
    });
  }

  return http.patch(api_documents(documentId), requestData).then(({ data }) => {
    dispatch(updateDocument(data));
    return data;
  });
};

export const deleteDocumentById = (dispatch, params = {}) => {
  const { documentId, typeId } = params;

  return http.delete(api_documents(documentId)).then(() => {
    dispatch(deleteDocument({ documentId, typeId }));
  });
};

export const deleteDocumentCategory = (dispatch, categoryId) => {
  return http.delete(api_document_categories(categoryId)).then(() => {
    dispatch(deleteCategory(categoryId));
  });
};

export default handleActions(reducers, initialState);

// ------------------------------------
// "plain" action functions
// ------------------------------------

export const clearDocumentCategories = () =>
  setCategories(initialState.documentCategories);
